import React from "react";
import {
  Target,
  ListChecks,
  CalendarRange,
  Wallet,
  MessagesSquare,
  LineChart,
} from "lucide-react";
import { Placeholder } from "./Placeholder";

const features = [
  {
    icon: Target,
    title: "Matched to real demand",
    body:
      "Your products are recommended to customers based on their interest and occasion matching, not just listed in a directory waiting to be found.",
  },
  {
    icon: ListChecks,
    title: "A simple four-step flow",
    body:
      "Receive an order, confirm it, mark it prepared, then dispatch or hand off for pickup. No ambiguity about what's expected next.",
  },
  {
    icon: CalendarRange,
    title: "Set your own terms",
    body:
      "Business hours, blackout dates, minimum notice per product, pickup availability and locations, all vendor-controlled.",
  },
  {
    icon: Wallet,
    title: "Transparent payouts",
    body:
      "See exactly what you're owed per order. Funds are held securely until fulfillment, then released to you, clear and on time.",
  },
  {
    icon: MessagesSquare,
    title: "Direct line to customers",
    body:
      "When something needs clarifying, message the customer about their specific order, scoped to that conversation, nothing else.",
  },
  {
    icon: LineChart,
    title: "A real dashboard",
    body:
      "Track fulfilled orders, revenue, and performance over time. The numbers you actually want to see, in one place.",
  },
];

const placeholders = [
  "Vendor order list",
  "Confirm / Mark Ready workflow",
  "Vendor home dashboard with revenue metrics",
];

export const ForVendors = () => {
  return (
    <section
      id="for-vendors"
      data-testid="for-vendors-section"
      className="liea-section"
      style={{
        background: "var(--liea-forest)",
        color: "var(--liea-cream-soft)",
      }}
    >
      <div className="liea-container">
        <div className="max-w-2xl">
          <span
            className="liea-eyebrow"
            style={{ color: "var(--liea-terracotta)" }}
          >
            For vendors
          </span>
          <h2
            className="mt-4 text-[36px] md:text-[48px] lg:text-[56px] leading-[1.05] tracking-tight"
            style={{ color: "var(--liea-cream-soft)" }}
          >
            Run your shop,{" "}
            <em
              style={{
                fontStyle: "italic",
                color: "var(--liea-terracotta)",
              }}
            >
              we&rsquo;ll send you customers
            </em>
            <span style={{ color: "var(--liea-terracotta)" }}>.</span>
          </h2>
          <p
            className="mt-5 text-base md:text-lg max-w-xl leading-relaxed"
            style={{ color: "rgba(235, 222, 209, 0.75)" }}
          >
            A clean order workflow, real demand routed to you, and payouts that
            arrive when they should.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: "rgba(212, 131, 79, 0.18)", border: "1px solid rgba(212, 131, 79, 0.35)" }}>
            <span className="text-sm font-medium" style={{ color: "var(--liea-terracotta)" }}>
              No signup fee. Free to join. We earn when you earn.
            </span>
          </div>
        </div>

        <div className="mt-14 md:mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`vendor-feature-${i + 1}`}
                className="p-7 rounded-2xl"
                style={{
                  background: "rgba(251, 247, 241, 0.06)",
                  border: "1px solid rgba(235, 222, 209, 0.14)",
                }}
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(212, 131, 79, 0.18)",
                    color: "var(--liea-terracotta)",
                  }}
                >
                  <Icon size={20} strokeWidth={1.7} />
                </div>
                <h3
                  className="mt-5 text-[22px] leading-snug"
                  style={{ color: "var(--liea-cream-soft)" }}
                >
                  {f.title}
                </h3>
                <p
                  className="mt-3 text-[15px] leading-relaxed"
                  style={{ color: "rgba(235, 222, 209, 0.7)" }}
                >
                  {f.body}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-14 md:mt-20 grid md:grid-cols-3 gap-6">
          {placeholders.map((p, i) => (
            <Placeholder
              key={p}
              testId={`vendor-placeholder-${i + 1}`}
              label={p}
              aspect="4/3"
            />
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-4">
          <a
            href="#faq"
            data-testid="vendor-cta-apply"
            className="liea-btn-accent inline-flex items-center px-6 py-3.5 rounded-full text-[15px] font-medium"
          >
            Apply to become a vendor
          </a>
          <span
            className="text-sm"
            style={{ color: "rgba(235, 222, 209, 0.6)" }}
          >
            Onboarding takes about a week
          </span>
        </div>
      </div>
    </section>
  );
};

export default ForVendors;
